import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '~/utils/cn';

const buttonVariants = cva(
  // `disabled:pointer-events-none` is intentionally omitted here so that the
  // `disabled:cursor-not-allowed` style is actually visible on hover (per
  // CLAUDE.md). Click prevention is already handled by the HTML `disabled`
  // attribute the consumer passes through; hover styles on disabled variants
  // are visually muted by `disabled:opacity-50`.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary CTA — uses the brand gradient (see .bg-gradient-btn in utilities.css).
        // The gradient + darker hover are defined on the utility itself, so no hover:* here.
        default: 'bg-gradient-btn text-primary-foreground',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        gradient:
          'bg-gradient-to-r from-primary to-secondary text-primary-foreground hover:opacity-90',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinning loader and disables the button while true. */
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, isLoading = false, disabled, children, ...props },
    ref,
  ) => {
    const cls = cn(buttonVariants({ variant, size, className }));

    // Slot (asChild) calls React.Children.only and throws if it receives more than one child.
    // Keep the two cases separate so Slot always gets exactly one child element.
    if (asChild) {
      return (
        <Slot className={cls} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button className={cls} ref={ref} disabled={disabled ?? isLoading} {...props}>
        {isLoading && <Loader2 className="animate-spin" />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
